// `AbortSignal.any` NEEDS SAFARI / WKWEBVIEW 17.4+ AND CHROME / ANDROID WEBVIEW 116+, AND
// `AbortSignal.timeout` SAFARI 16+ / CHROME 103+. THE NATIVE SHELLS LOAD THIS APP IN WHATEVER
// WEBVIEW THE DEVICE HAS, SO BOTH ARE BUILT FROM A PLAIN `AbortController` INSTEAD.

export interface LinkedSignal {
  signal: AbortSignal;
  // DETACHES THE LISTENERS AND CLEARS THE TIMER; CALL IT WHEN THE REQUEST SETTLES
  dispose: () => void;
}

// ABORTS WHEN ANY OF `signals` ABORTS OR, WITH `timeoutMs`, WHEN THE TIME RUNS OUT (WITH A
// "TimeoutError" REASON, AS `AbortSignal.timeout` GIVES). THE TIMER IS A DELIBERATE DELAY - THE
// REQUEST CEILING - NOT A WAY OF SEQUENCING STATE.
export function linkSignals(signals: Array<AbortSignal | undefined>, timeoutMs?: number): LinkedSignal {
  const controller = new AbortController();
  const cleanups: Array<() => void> = [];
  const dispose = () => {
    for (const c of cleanups.splice(0)) c();
  };
  const abort = (reason: unknown) => {
    if (controller.signal.aborted) return;
    controller.abort(reason);
    dispose();
  };

  for (const s of signals) {
    if (!s) continue;
    if (s.aborted) {
      abort(s.reason);
      return { signal: controller.signal, dispose };
    }
    const onAbort = () => abort(s.reason);
    s.addEventListener("abort", onAbort, { once: true });
    cleanups.push(() => s.removeEventListener("abort", onAbort));
  }

  if (timeoutMs !== undefined) {
    const timer = setTimeout(() => abort(new DOMException("The operation timed out.", "TimeoutError")), timeoutMs);
    cleanups.push(() => clearTimeout(timer));
  }

  return { signal: controller.signal, dispose };
}
